import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion';
import routes from '../../../Routes/Routes.module.css';
import styles from './ImprovementPlanView.module.css';
import Tile from '../../../Components/Utils/Tile/Tile';
import TileColumn from '../../../Components/Utils/Tile/TileColumn/TileColumn';
import PlanHeader from '../../../Components/Improve/ImprovementPlanView/PlanHeader/PlanHeader';
import PlanTitle from '../../../Components/Improve/ImprovementPlanView/PlanTitle/PlanTitle';
import PlanDetails from '../../../Components/Improve/ImprovementPlanView/PlanDetails/PlanDetails';
import ContextMenu from '../../../Components/Utils/ContextMenu/ContextMenu';
import { ContextMenuItem } from '../../../Types/ContextMenuItem';
import TileRow from '../../../Components/Utils/Tile/TileRow/TileRow';
import ProgressBar from '../../../Components/Utils/ProgressBar/ProgressBar';
import ObjectiveDetailsCard from '../../../Components/Improve/ImprovementPlanView/ObjectiveDetailsCard/ObjectiveDetailsCard';
import TasksPane from '../../../Components/Improve/ImprovementPlanView/TasksPane/TasksPane';
import TaskDetailsPane from '../../../Components/Improve/ImprovementPlanView/TaskDetailsPane/TaskDetailsPane';
import ResizeHandle from '../../../Components/Utils/ResizablePane/ResizeHandle';
import { useResizablePane } from '../../../Components/Utils/ResizablePane/useResizablePane';
import { ImprovementDetail, ImprovementPlanDetail, ImprovementPlanProgress, ImproveTaskDetail } from '../../../Types/Improve/ImprovementPlanDetail';
import { GetImprovementPlanDetailById, CreateImprovementByTitle, UpdateImproveAreaTitle, GetImproveLinkedEvidence, CreateImproveTaskByTitle, UpdateImprovementByDetail, UpdateImproveTaskByDetail, GetImprovementPlanProgress } from '../../../API/Improve/Improve';
import { useParams } from 'react-router-dom';
import CreationBar from '../../../Components/Utils/Actions/CreationBar/CreationBar';
import EditableTextField from '../../../Components/Utils/EditableTextField/EditableTextField';
import TabbedView from '../../../Components/Utils/TabbedView/TabbedView';
import { ITab } from '../../../Types/ITab';
import { FileData } from '../../../Components/Utils/FileItem/FileItem';
import LinkedEvidencePopup from '../../../Components/Utils/LinkedEvidencePopup/LinkedEvidencePopup';
import ReusableCommentsPopup from '../../../Components/Utils/CommentsPopup/ReusableCommentsPopup';
import Popup from '../../../Components/Utils/Popup/Popup';
import InviteRemoveUsers from '../../../Components/Utils/InviteRemoveUsers/InviteRemoveUsers';
import ChangeOwnerPopup from '../../../Components/Utils/ChangeOwnerPopup/ChangeOwnerPopup';
import { EvaluateRolePermission } from '../../../Types/Roles/RolePermission';
import { UserDisplay } from '../../../Types/User';
import ProgressSection from '../../../Components/Utils/ProgressSection/ProgressSection';
import Tooltip from '../../../Components/Utils/Tooltip/Tooltip';
import CompactFilters, { ISelectOption, FilterConfig } from '../../../Components/Analyse/CompactFilters/CompactFilters';
import Separator from '../../../Components/Utils/Separator/Separator';
import { ImproveTemplateDto } from '../../../Types/Improve/ImproveTemplateDto';
import type { AreaKpiSummary } from '../../../Types/KPI/IAreaKpiSummary';
import { axiosInstance } from '../../../Authentication/AxiosInterceptor';
import ConfirmAction from '../../../Components/Utils/ConfirmAction/ConfirmAction';
import { getTaskProgress } from '../../../Helpers/Tasks';
import { isSetDate, toDate } from '../../../Helpers/Dates';
import { CommentType } from '../../../Types/CreateCommentRequest';
import { ProductType } from '../../../Types/ProductType';
import { useAppSelector } from '../../../Redux/Store/Store';
import { getClient } from '../../../Redux/Store/Slices/client';
import DiagnosticLinkedKpis from '../../../Components/SectionsDrawer/DiagnosticLinkedKpis';
import KpiAreaSummaryModal from '../../../Components/KPI/KpiAreaSummaryModal';
import { hasKpiEditPermission } from '../../../Helpers/kpiPermissions';
import { useClientKpiAccess } from '../../../Hooks/useClientKpiAccess';
import LinkedDiagnosticsPopup from '../../../Components/Improve/ImprovementPlanView/LinkedDiagnosticsPopup/LinkedDiagnosticsPopup';
import ImproveExportButton from '../../../Components/Utils/ExportButton/ImproveExportButton';

// Filter option constants
const STATUS_OPTIONS: ISelectOption[] = [
  { label: 'Complete', value: 'Complete', required: false },
  { label: 'In progress', value: 'In progress', required: false },
  { label: 'Not started', value: 'Not started', required: false },
  { label: 'Overdue', value: 'Overdue', required: false },
];

const IMPACT_OPTIONS: ISelectOption[] = [
  { label: 'Exceeded', value: 'Exceeded', required: false },
  { label: 'Met', value: 'Met', required: false },
  { label: 'Partial', value: 'Partial', required: false },
  { label: 'Not Met', value: 'Not_Met', required: false },
  { label: 'Impact not set', value: 'Impact not set', required: false },
];

const IMPACT_NOT_SET_VALUE = 'Impact not set';

// Helper functions for filtering
const hasNoImpact = (impact: string | undefined): boolean => {
  return !impact || (typeof impact === 'string' && impact.trim() === '');
};

const matchesStatusFilter = (status: string | undefined, selectedStatuses: string[]): boolean => {
  if (selectedStatuses.length === 0) return true;

  const normaliseStatus = (value: string | undefined): string => {
    if (!value || value.trim() === '') return 'not started';
    return value.trim().toLowerCase().replace(/\s+/g, ' ');
  };

  const targetStatus = normaliseStatus(status);
  const selectedStatusValues = selectedStatuses.map(s => normaliseStatus(s));

  return selectedStatusValues.includes(targetStatus);
};

const matchesImpactFilter = (actualImpact: string | undefined, selectedImpacts: string[]): boolean => {
  if (selectedImpacts.length === 0) return true;
  
  const hasImpactNotSetSelected = selectedImpacts.includes(IMPACT_NOT_SET_VALUE);
  const otherImpactValues = selectedImpacts.filter(v => v !== IMPACT_NOT_SET_VALUE);
  const objectiveHasNoImpact = hasNoImpact(actualImpact);
  
  if (hasImpactNotSetSelected && otherImpactValues.length > 0) {
    // Show objectives without impact OR matching other selected values
    return objectiveHasNoImpact || (actualImpact !== undefined && otherImpactValues.includes(actualImpact));
  } else if (hasImpactNotSetSelected) {
    // Only show objectives without impact
    return objectiveHasNoImpact;
  } else {
    // Filter by specific impact values
    return actualImpact !== undefined && selectedImpacts.includes(actualImpact);
  }
};

const matchesAssignedToFilter = (assignedTo: UserDisplay[] | undefined, selectedUserSubs: string[]): boolean => {
  if (selectedUserSubs.length === 0) return true;
  return assignedTo !== undefined && assignedTo.some(user => selectedUserSubs.includes(user.sub));
};

const matchesTaskAssignedToFilter = (tasks: ImproveTaskDetail[] | undefined, selectedUserSubs: string[]): boolean => {
  if (selectedUserSubs.length === 0) return true;
  return (tasks ?? []).some(task => !!task.assignedTo?.sub && selectedUserSubs.includes(task.assignedTo.sub));
};

const ImprovementPlanView = () => {

  const { planId } = useParams<{ planId: string }>();
  const client = useAppSelector(getClient);
  const currentUserSub = useAppSelector((state) => state.auth.user?.sub);
  const { canAccessClientKpi, kpiDisabledReason } = useClientKpiAccess();

  const [rolePermissions, setRolePermissions] = useState<EvaluateRolePermission>();

  const [improvementPlan, setImprovementPlan] = useState<ImprovementPlanDetail>();
  const [activeObjective, setActiveObjective] = useState<ImprovementDetail>();
  const [activeTask, setActiveTask] = useState<ImproveTaskDetail>();
  const [archived, setArchived] = useState<boolean>(false);

  const [files, setFiles] = useState<FileData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileCount, setFileCount] = useState(0);
  const [evidenceVisible, setEvidenceVisible] = useState(false);
  const [evidenceObjectiveId, setEvidenceObjectiveId] = useState<string | undefined>();
  const [evidenceRefresh, setEvidenceRefresh] = useState(0);
  const [linkedDiagnosticsVisible, setLinkedDiagnosticsVisible] = useState(false);
  const [linkedDiagnosticsObjectiveId, setLinkedDiagnosticsObjectiveId] = useState<string | undefined>();
  const [commentsVisible, setCommentsVisible] = React.useState(false);
  const [progressNotesVisible, setProgressNotesVisible] = React.useState(false);
  const [taskCommentsVisible, setTaskCommentsVisible] = React.useState(false);
  const [taskIdsWithComments, setTaskIdsWithComments] = useState<Set<string>>(() => new Set());

  const [kpiListPaneOpen, setKpiListPaneOpen] = useState(false);
  const [kpiSummaryOpen, setKpiSummaryOpen] = useState(false);
  const [kpiInitialKeyResultId, setKpiInitialKeyResultId] = useState<string | null>(null);
  const [kpiRefreshToken, setKpiRefreshToken] = useState(0);
  const [kpiSummary, setKpiSummary] = useState<AreaKpiSummary | undefined>(undefined);

  const [availableUsers, setAvailableUsers] = useState<UserDisplay[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  const [template, setTemplate] = useState<ImproveTemplateDto>();

  const [progress, setProgress] = useState<ImprovementPlanProgress>();

  useEffect(() => {
    // update active objective/task when plan changes
    if (activeObjective) {
      const updatedObjective = improvementPlan?.improvements.find(imp => imp.id === activeObjective.id);
      setActiveObjective(updatedObjective);
    }
    if (activeTask) {
      const updatedTask = improvementPlan?.improvements.flatMap(imp => imp.tasks || []).find(t => t.uniqueId === activeTask.uniqueId);
      setActiveTask(updatedTask);
    }
  }, [improvementPlan]);
  
  // Filter state
  const [selectedFilters, setSelectedFilters] = useState<Record<string, ISelectOption[]>>({
    status: [],
    impact: [],
    assignedTo: []
  });

  const loadEvidence = async (visible: boolean, objectiveId?: string) => {
    if (!visible) {
      setEvidenceVisible(false);
      setEvidenceObjectiveId(undefined);
      return;
    }

    // Store the objectiveId and trigger the useEffect to load
    setEvidenceObjectiveId(objectiveId);
    setEvidenceVisible(true);
  };

  const handleLinkedDiagnosticsVisible = (visible: boolean, objectiveId?: string) => {
    if (!visible) {
      setLinkedDiagnosticsVisible(false);
      setLinkedDiagnosticsObjectiveId(undefined);
      return;
    }

    setLinkedDiagnosticsObjectiveId(objectiveId);
    setLinkedDiagnosticsVisible(true);
  };

  useEffect(() => {
    const fetchEvidence = async () => {
      if (!evidenceVisible || !planId) {
        return;
      }

      // Use provided objectiveId or fall back to activeObjective
      const targetObjectiveId = evidenceObjectiveId || activeObjective?.id;
      if (!targetObjectiveId) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await GetImproveLinkedEvidence(planId, targetObjectiveId);

        setFiles(response.files || []);
        setFileCount(response.fileCount || 0);
      } catch (err) {
        console.error('Error fetching linked evidence:', err);
        setError('Failed to load linked evidence files. Please try again.');
        setFiles([]);
        setFileCount(0);
        setEvidenceVisible(false);
      } finally {
        setLoading(false);
      }
    };

    fetchEvidence();
  }, [evidenceVisible, evidenceObjectiveId, planId, activeObjective?.id, evidenceRefresh]);

  const [showViewUsersPopup, setShowViewUsersPopup] = useState<boolean>(false);
  const [showChangeOwnerPopup, setShowChangeOwnerPopup] = useState<boolean>(false);

  const [tabData, setTabData] = useState<ITab[]>([
    { title: 'Active', active: true, url: '' },
    { title: 'Archived', active: false, url: '' }
  ]);

  const getImprovementPlan = useCallback(async () => {
    if (planId) {
      const response = await GetImprovementPlanDetailById(planId, archived);
      setImprovementPlan(response.improvementPlanDetail);
      setRolePermissions(response.rolePermissions);
      setAvailableUsers(response.availableUsers);
      setAvailableTags(response.tags);
      setTemplate(response.template);
      setProgress(response);
      setKpiSummary(response.kpiSummary);
    }
  }, [planId, archived]);

  useEffect(() => {
    getImprovementPlan();
  }, [getImprovementPlan]);

  const openKpiSummary = useCallback((keyResultId?: string) => {
    if (!canAccessClientKpi) return;
    setKpiInitialKeyResultId(keyResultId ?? null);
    setKpiSummaryOpen(true);
  }, [canAccessClientKpi]);

  useEffect(() => {
    if (!canAccessClientKpi) {
      setKpiListPaneOpen(false);
    }
  }, [canAccessClientKpi]);

  const closeKpiSummary = useCallback(() => {
    setKpiSummaryOpen(false);
    setKpiInitialKeyResultId(null);
    setKpiRefreshToken((t) => t + 1);
    void getImprovementPlan();
  }, [getImprovementPlan]);

  const handleObjectiveClick = (objectiveId: string) => {
    if (activeObjective?.id === objectiveId) {
      setActiveObjective(undefined);
    } else {


      const objective = improvementPlan?.improvements.find(imp => imp.id === objectiveId);
      setActiveObjective(objective);
    }
    setActiveTask(undefined);
  };

  const handleTaskClick = (taskId: string) => {
    if (activeTask?.uniqueId === taskId) {
      setActiveTask(undefined);
    } else {
      const task = activeObjective?.tasks.find(t => t.uniqueId === taskId) || undefined;
      setActiveTask(task);
    }
  };

  const handleArchiveTabChanged = (tab: ITab) => {
    const isArchived = tab.title === 'Archived';
    setArchived(isArchived);
    setTabData(prevData =>
      prevData.map(t => ({
        ...t,
        active: t.title === tab.title
      }))
    );
    setActiveObjective(undefined);
    setActiveTask(undefined);
  }

  const getParsedDate = (strDate: Date | undefined) => {
    if (!strDate) {
      return 'N/A';
    }
    const date = new Date(strDate);
    if (isNaN(date.getTime()) || date.getFullYear() <= 1) {
      return 'N/A';
    }
    const dateFormatter = new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    return dateFormatter.format(date);
  }

  const completedObjectives = improvementPlan?.improvements?.filter(imp => isSetDate(imp.completionDate)).length ?? 0;
  const totalObjectives = improvementPlan?.improvements?.length ?? 0;

  const completedTasks = improvementPlan?.improvements?.reduce((count, imp) => {
    const tasks = imp.tasks ?? [];
    const completed = tasks.filter(
      task => getTaskProgress(task) === "Complete"
    ).length;
    return count + completed;
  }, 0) ?? 0;
  const totalTasks = improvementPlan?.improvements?.reduce((count, imp) => {
    const tasks = imp.tasks ?? [];
    return count + tasks.length;
  }, 0) ?? 0;

  // Filter configurations
  const filterConfigs: FilterConfig[] = useMemo(() => [
    {
      key: 'status',
      label: 'Status',
      placeholder: 'Select status',
      dataSource: 'props',
      data: STATUS_OPTIONS
    },
    {
      key: 'impact',
      label: 'Impact',
      placeholder: 'Select impact',
      dataSource: 'props',
      data: IMPACT_OPTIONS
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      placeholder: 'Select users',
      dataSource: 'props',
      data: availableUsers.map(user => ({
        label: user.fullName,
        value: user.sub,
        required: false
      }))
    }
  ], [availableUsers]);

  // Check if there are any active filters
  const hasActiveFilters = useMemo(() => {
    return selectedFilters.status.length > 0 || 
           selectedFilters.impact.length > 0 || 
           selectedFilters.assignedTo.length > 0;
  }, [selectedFilters]);

  // Compute which objectives match the filters and separate into matching/non-matching
  const { matchingObjectives, nonMatchingObjectives } = useMemo(() => {
    if (!improvementPlan?.improvements) return { matchingObjectives: [], nonMatchingObjectives: [] };
    
    const statusValues = selectedFilters.status.map(f => f.value);
    const impactValues = selectedFilters.impact.map(f => f.value);
    const assignedToSubs = selectedFilters.assignedTo.map(f => f.value);
    
    const matching: ImprovementDetail[] = [];
    const nonMatching: ImprovementDetail[] = [];
    
    for (const obj of improvementPlan.improvements) {
      const matches = matchesStatusFilter(obj.status, statusValues) &&
                      matchesImpactFilter(obj.actualImpact, impactValues) &&
                      (
                        matchesAssignedToFilter(obj.assignedTo, assignedToSubs) ||
                        matchesTaskAssignedToFilter(obj.tasks, assignedToSubs)
                      );
      
      if (matches) {
        matching.push(obj);
      } else {
        nonMatching.push(obj);
      }
    }
    
    return { matchingObjectives: matching, nonMatchingObjectives: nonMatching };
  }, [improvementPlan?.improvements, selectedFilters]);

  const handleCreateObjective = async (title: string) => {
    if (planId) {
      try {
        const newObjective = await CreateImprovementByTitle(planId, title);

        setImprovementPlan(prevPlan => {
          if (!prevPlan) return prevPlan;
          return {
            ...prevPlan,
            improvements: [...(prevPlan.improvements || []), newObjective]
          };
        });

      } catch (error) {
        console.error('Error creating objective:', error);
      }
    }
  };

  const handleCreateTask = async (title: string, improvementId: string) => {
    if (improvementId) {
      try {
        const newTask = await CreateImproveTaskByTitle(improvementId, title);

        setImprovementPlan(prevPlan => {
          if (!prevPlan) return prevPlan;
          const updatedImprovements = prevPlan.improvements.map(imp => {
            if (imp.id === improvementId) {
              return {
                ...imp,
                tasks: [...(imp.tasks || []), newTask]
              };
            }
            return imp;
          });
          return {
            ...prevPlan,
            improvements: updatedImprovements
          };
        });
        activeObjective && setActiveObjective(prevObjective => {
          if (!prevObjective) return prevObjective;
          if (prevObjective.id === improvementId) {
            return {
              ...prevObjective,
              tasks: [...(prevObjective.tasks || []), newTask]
            };
          }
          return prevObjective;
        });

      } catch (error) {
        console.error('Error creating objective:', error);
      }
    }
  };

  const handleTitleUpdate = async (newTitle: string) => {
    if (planId && newTitle !== improvementPlan?.title) {
      try {
        await UpdateImproveAreaTitle(planId, newTitle);
        await getImprovementPlan();
      } catch (error) {
        console.error('Error updating title:', error);
      }
    }
  };

  const handleToggleViewUsers = () => {
    setShowViewUsersPopup(true);
  };

  const handleToggleChangeOwner = () => {
    setShowChangeOwnerPopup(true);
  };

  const contextMenuItems: ContextMenuItem[] = useMemo(
    () => [
      {
        title: 'View Users',
        icon: "/img/plus-minus.svg",
        disabled: false,
        onClickHandler: () => {
          handleToggleViewUsers();
        },
      },
      {
        title: 'Change Owner',
        icon: "/img/change.svg",
        disabled: !rolePermissions?.changeOwner,
        disabledTooltip: `Your role as ${rolePermissions?.roleTitle} means you cannot change the owner`,
        onClickHandler: () => {
          handleToggleChangeOwner();
        },
      },
    ],
    [rolePermissions]
  );

  const handleObjectiveSave = async (updated: ImprovementDetail) => {
    console.log(`updated objective to save:`, updated);
    var newVersion = await UpdateImprovementByDetail(updated);
    //updated.version = newVersion.version;
    handleObjectiveUpdated(newVersion);

    if(planId){
      const planProgress = await GetImprovementPlanProgress(planId!);
      setProgress(planProgress);
    } 
  };

  const getComputedObjectiveStatus = (objective: ImprovementDetail): 'Complete' | 'Overdue' | 'In Progress' | 'Not Started' => {
    if (isSetDate(objective.completionDate)) {
      return 'Complete';
    }

    if (isSetDate(objective.targetDate) && new Date() > toDate(objective.targetDate!)) {
      return 'Overdue';
    }

    if ((objective.status ?? '').trim().toLowerCase() === 'in progress') {
      return 'In Progress';
    }

    return 'Not Started';
  };

  const updateObjectiveStatusFromComments = async (objective: ImprovementDetail | undefined, commentsCount: number) => {
    if (!objective || commentsCount <= 0) {
      return;
    }

    if (getComputedObjectiveStatus(objective) !== 'Not Started') {
      return;
    }

    await handleObjectiveSave({ ...objective, status: 'In Progress' });
  };

  const syncTaskCommentFlags = useCallback(async (tasks: ImproveTaskDetail[]) => {
    if (tasks.length === 0) {
      setTaskIdsWithComments(new Set());
      return;
    }

    const results = await Promise.all(
      tasks.map(async (task) => {
        const contextId = task.commentContextId || task.uniqueId;
        try {
          const res = await axiosInstance.get<unknown[]>(
            `GetSummariseCommentsByCriteriaId/${contextId}/${CommentType.Comment}`
          );
          return {
            id: task.uniqueId,
            hasComments: (res.data?.length ?? 0) > 0,
          };
        } catch {
          return { id: task.uniqueId, hasComments: false };
        }
      })
    );

    setTaskIdsWithComments(
      new Set(results.filter((r) => r.hasComments).map((r) => r.id))
    );
  }, []);

  const activeObjectiveTaskIdsKey = useMemo(
    () => (activeObjective?.tasks ?? []).map((t) => t.uniqueId).sort().join('|'),
    [activeObjective?.id, activeObjective?.tasks]
  );

  useEffect(() => {
    if (!activeObjective?.id || !activeObjective.tasks?.length) {
      setTaskIdsWithComments(new Set());
      return;
    }

    void syncTaskCommentFlags(activeObjective.tasks);
  }, [activeObjective?.id, activeObjectiveTaskIdsKey, syncTaskCommentFlags]);

  const handleTaskCommentsChange = useCallback((taskId: string, commentsCount: number) => {
    setTaskIdsWithComments((prev) => {
      const hasComments = commentsCount > 0;
      if (hasComments === prev.has(taskId)) {
        return prev;
      }

      const next = new Set(prev);
      if (hasComments) {
        next.add(taskId);
      } else {
        next.delete(taskId);
      }
      return next;
    });
  }, []);

  const handleTaskCommentsPopupChange = useCallback(
    (comments: { length: number }) => {
      if (activeTask?.uniqueId) {
        handleTaskCommentsChange(activeTask.uniqueId, comments.length);
      }
    },
    [activeTask?.uniqueId, handleTaskCommentsChange]
  );

  const handleObjectiveUpdated = async (updated: ImprovementDetail) => {

    console.log('Objective updated called with updated:', updated);

    setActiveObjective(updated);

    setImprovementPlan((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        improvements: prev.improvements.map((imp) =>
          imp.id === updated.id ? updated : imp
        )
      };
    });
  };

  const handleTaskSave = async (updated: ImproveTaskDetail) => {
    var newVersion =  await UpdateImproveTaskByDetail(updated);
    //updated.version = newVersion.version;
    handleTaskUpdated(newVersion);

    if(planId){
      const planProgress = await GetImprovementPlanProgress(planId!);
      setProgress(planProgress);
    } 
  }

const handleTaskUpdated = async (updated: ImproveTaskDetail) => {

  console.log('Task updated called with updated:', updated);
  // update full plan list
  setImprovementPlan(prev => {
    if (!prev) return prev;

    return {
      ...prev,
      improvements: prev.improvements.map(imp => ({
        ...imp,
        tasks: (imp.tasks ?? []).map(t =>
          t.uniqueId === updated.uniqueId ? { ...updated } : t
        )
      }))
    };
  });

  // update active objective's task list (so left-pane + tasks list stay correct)
  setActiveObjective(prev => {
    if (!prev) return prev;
    return {
      ...prev,
      tasks: (prev.tasks ?? []).map(t =>
        t.uniqueId === updated.uniqueId ? { ...updated } : t
      )
    };
  });

  // update the active task itself (so TaskDetailsPane view mode reflects immediately)
  setActiveTask(prev =>
    prev?.uniqueId === updated.uniqueId ? { ...updated } : prev
  );
};

  console.log('Improvement Plan:', improvementPlan);

  const handleTaskCommentsVisible = (taskId: string) => {
    setActiveTask(improvementPlan?.improvements.flatMap(imp => imp.tasks || []).find(t => t.uniqueId === taskId));
    setActiveObjective(improvementPlan?.improvements.find(imp => imp.tasks?.some(t => t.uniqueId === taskId)));
    setTaskCommentsVisible(true);
  }

  const handleObjectiveCommentsVisible = (objectiveId: string) => {
    setActiveObjective(improvementPlan?.improvements.find(imp => imp.id === objectiveId));
    setCommentsVisible(true);
  }

  const [pendingItem, setPendingItem] = useState<string>();
  
  const showDeleteConfirm = (objectiveId: string) => {
    setPendingItem(objectiveId);
  }

  const deleteObjectiveHandler = async () => {
    if (!pendingItem) return;
    
    await axiosInstance.get(`Improve/DeleteImprovement/${pendingItem}`);
    
    // Refresh the improvement plan data
    await getImprovementPlan();

    setPendingItem(undefined);
  }

  const hasPanesOpen = !!(activeObjective || activeTask || kpiListPaneOpen);

  const objectivePane = useResizablePane({
    storageKey: 'improve.plan.objectivePaneWidth',
    defaultWidth: 400,
  });
  const taskPane = useResizablePane({
    storageKey: 'improve.plan.taskPaneWidth',
    defaultWidth: 400,
  });

  return (
    <div className={`${routes.container} ${routes.row} ${hasPanesOpen ? styles.withPanes : ''}`}>
      {activeObjective && planId &&
        <Popup isVisible={commentsVisible} onClose={() => setCommentsVisible(false)}>
          <ReusableCommentsPopup
            contextId={activeObjective.id}
            authorizationContextId={planId}
            progressMode={false}
            onCommentsChange={(comments) => updateObjectiveStatusFromComments(activeObjective, comments.length)}
          />
        </Popup>
      }

      {activeObjective && planId &&
        <Popup isVisible={progressNotesVisible} onClose={() => setProgressNotesVisible(false)}>
          <ReusableCommentsPopup
            contextId={activeObjective.id}
            authorizationContextId={planId}
            progressMode={true}
            onCommentsChange={(comments) => updateObjectiveStatusFromComments(activeObjective, comments.length)}
          />
        </Popup>
      }

      {activeTask && planId &&
        <Popup isVisible={taskCommentsVisible} onClose={() => setTaskCommentsVisible(false)}>
          <ReusableCommentsPopup
            contextId={activeTask.commentContextId || activeTask.uniqueId}
            authorizationContextId={planId}
            progressMode={false}
            onCommentsChange={handleTaskCommentsPopupChange}
          />
        </Popup>
      }

      <section className={routes.left}>
        <section className={routes.body}>
          <div className={styles.mainContentWrapper}>
            <Tile>
              <TileColumn>
                <PlanHeader
                  progressContent={<p>Progress badge here</p>}
                  actionsContent={(
                    <div className={styles.documentActionsGroup} aria-label="Plan actions">
                      {planId && client?.id && (
                        <Tooltip
                          text={kpiDisabledReason}
                          align="top"
                          disabled={canAccessClientKpi}
                        >
                          <div className={styles.documentActionItem}>
                            <button
                              type="button"
                              className={styles.documentActionButton}
                              disabled={!canAccessClientKpi}
                              onClick={() => setKpiListPaneOpen(true)}
                            >
                              <span>Linked KPIs</span>
                              <img
                                src="/img/NavIcons/kpi.svg"
                                alt=""
                                className={styles.documentActionIcon}
                                aria-hidden
                              />
                            </button>
                          </div>
                        </Tooltip>
                      )}
                      {planId && client?.id && (
                        <span className={styles.documentActionsDivider} aria-hidden />
                      )}
                      {planId && (
                        <div className={styles.documentActionExport}>
                          <Tooltip
                            text={`Your role as a ${rolePermissions?.roleTitle} means you cannot export this plan`}
                            align="top"
                            sideAlign="left"
                            disabled={rolePermissions?.exportDiagnostic}
                          >
                            <ImproveExportButton
                              areaId={planId}
                              label="Export"
                              size={null}
                              embedded
                              template={template}
                              disabled={!rolePermissions?.exportDiagnostic}
                              archivedPlans={archived}
                            />
                          </Tooltip>
                        </div>
                      )}
                      <span className={styles.documentActionsDivider} aria-hidden />
                      <div className={styles.documentActionGear}>
                        <ContextMenu
                          contextId="improvement-plan-actions"
                          items={contextMenuItems}
                          position="left"
                        >
                          <img
                            className={styles.gear}
                            src="/img/gear.svg"
                            alt="Plan actions"
                          />
                        </ContextMenu>
                      </div>
                    </div>
                  )}
                />
                <Popup isVisible={showViewUsersPopup} onClose={() => setShowViewUsersPopup(false)}>
                  <InviteRemoveUsers
                    areaId={planId}
                    productType={ProductType.Improve}
                    rolePermissions={rolePermissions}
                    onAreaUsersChanged={getImprovementPlan}
                  />
                </Popup>
                {planId && (
                  <Popup isVisible={showChangeOwnerPopup} onClose={() => setShowChangeOwnerPopup(false)}>
                    <ChangeOwnerPopup
                      areaId={planId}
                      canChangeOwner={rolePermissions?.changeOwner}
                      onOwnerChanged={getImprovementPlan}
                      onClose={() => setShowChangeOwnerPopup(false)}
                    />
                  </Popup>
                )}

                <EditableTextField
                  value={improvementPlan?.title || ''}
                  onChange={handleTitleUpdate}
                  canEdit={rolePermissions?.editTitle}
                  placeholder=""
                  className={styles.titleInput}
                  disabledTooltip={`Your role as ${rolePermissions?.roleTitle} means that you cannot edit the title`}
                />
                
              </TileColumn>
            </Tile>
            <section className={styles.progressSection}>

              <ProgressSection
                title="Objectives"
                itemName="objectives"
                segments={progress?.objectiveSegments}
                completedCount={completedObjectives}
                totalCount={totalObjectives}
              />

              <ProgressSection
                title="Tasks"
                itemName="tasks"
                segments={progress?.taskSegments}
                completedCount={completedTasks}
                totalCount={totalTasks}
              />

              <ProgressSection
                title="Current impact"
                itemName="impact"
                segments={progress?.impactSegments}
              />
            </section>
            <section className={styles.objectivesSection}>
            <div className={styles.objectivesHeader}>
              <h2 className={styles.objectivesTitle}>Objectives</h2>
              <TabbedView
                data={tabData}
                activeTab={tabData.find(tab => tab.active) || tabData[0]}
                activeTabChanged={handleArchiveTabChanged}
                disableNavigation
              />
            </div>
            <CreationBar
              onCreateHandler={handleCreateObjective}
              placeholder="Enter improvement title..."
              noTextMessage="Please enter an improvement title"
              disabled={archived}
            />
            <CompactFilters
              filters={filterConfigs}
              selectedFilters={selectedFilters}
              onFilterChange={setSelectedFilters}
            />
            {/* Render matching objectives */}
            {matchingObjectives.map((objective) => (
              <ObjectiveDetailsCard
                key={objective.id}
                objective={objective}
                isActive={activeObjective?.id === objective.id}
                onClickHandler={() => handleObjectiveClick(objective.id)}
                archived={archived}
                onArchiveToggle={getImprovementPlan}
                setEvidenceVisible={loadEvidence}
                setCommentsVisible={() => handleObjectiveCommentsVisible(objective.id)}
                setLinkedDiagnosticsVisible={handleLinkedDiagnosticsVisible}
                canArchive={rolePermissions?.editGeneratedContent || false}
                roleName={rolePermissions?.roleTitle || ''}
                editMode={rolePermissions?.editGeneratedContent || false}
                onDelete={showDeleteConfirm}
              />
            ))}
            
            {/* Add separator if there are both matching and non-matching objectives */}
            {hasActiveFilters && matchingObjectives.length > 0 && nonMatchingObjectives.length > 0 && (
              <Separator text="No matches found" />
            )}
            
            {/* Render non-matching objectives (greyed out) */}
            {nonMatchingObjectives.map((objective) => (
              <div key={objective.id} className={styles.disabledObjective}>
                <ObjectiveDetailsCard
                  objective={objective}
                  isActive={activeObjective?.id === objective.id}
                  onClickHandler={() => handleObjectiveClick(objective.id)}
                  archived={archived}
                  onArchiveToggle={getImprovementPlan}
                  setEvidenceVisible={loadEvidence}
                  setCommentsVisible={() => handleObjectiveCommentsVisible(objective.id)}
                  setLinkedDiagnosticsVisible={handleLinkedDiagnosticsVisible}
                  canArchive={rolePermissions?.editGeneratedContent || false}
                  roleName={rolePermissions?.roleTitle || ''}
                />
              </div>
            ))}
            </section>
          </div>
        </section>
      </section>

      <AnimatePresence mode="wait">
        {kpiListPaneOpen && canAccessClientKpi && planId && client?.id && (
          <motion.section
            id="improve-plan-linked-kpis-pane"
            className={styles.kpiListRight}
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 400, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{
              width: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            style={{ zIndex: 9998 }}
          >
            <div className={styles.kpiListPaneChrome}>
              <div className={styles.kpiListPaneHeader}>
                <h2 className={styles.kpiListPaneTitle}>Linked KPIs</h2>
                <button
                  type="button"
                  className={styles.kpiListPaneClose}
                  onClick={() => setKpiListPaneOpen(false)}
                  aria-label="Close linked KPIs"
                >
                  ×
                </button>
              </div>
              <DiagnosticLinkedKpis
                areaId={planId}
                areaType="Improve"
                refreshToken={kpiRefreshToken}
                prefetchedSummary={kpiSummary}
                hasKpiEdit={hasKpiEditPermission(rolePermissions)}
                onOpenKpiSummary={openKpiSummary}
                embedInPlanPane
              />
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      <Popup isVisible={kpiSummaryOpen} onClose={closeKpiSummary} allowSnapToSide>
        {planId && client?.id ? (
          <KpiAreaSummaryModal
            active={kpiSummaryOpen}
            areaId={planId}
            areaType="Improve"
            prefetchedSummary={kpiSummary}
            clientId={client.id}
            initialKeyResultId={kpiInitialKeyResultId}
            hasKpiEdit={hasKpiEditPermission(rolePermissions)}
            roleTitle={rolePermissions?.roleTitle}
            onClose={closeKpiSummary}
            areaBadgeLabel={improvementPlan?.title?.trim() || undefined}
          />
        ) : null}
      </Popup>

      <AnimatePresence mode="wait">
        {activeObjective && (
          <motion.section
            className={`${styles.tasksRight}`}
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: objectivePane.width, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{
              // No spring while dragging, or the pane lags behind the pointer.
              width: objectivePane.isResizing
                ? { duration: 0 }
                : { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            style={{ zIndex: 9999 }}
          >
            <ResizeHandle pane={objectivePane} label="Resize objective panel" />
            <TasksPane
              objective={activeObjective}
              tasks={activeObjective.tasks}
              activeTaskId={activeTask?.uniqueId}
              onTaskClick={handleTaskClick}
              setProgressNotesVisible={setProgressNotesVisible}
              onCreateTask={(title) => handleCreateTask(title, activeObjective?.id)}
              availableTags={availableTags}
              availableUsers={availableUsers}
              onObjectiveUpdated={handleObjectiveUpdated}
              saveObjectiveChanges={handleObjectiveSave}
              setTaskCommentsVisible={handleTaskCommentsVisible}
              editMode={rolePermissions?.editGeneratedContent || false}
              roleName={rolePermissions?.roleTitle || ''}
              template={template}
              assignedToFilterSubs={selectedFilters.assignedTo.map(f => f.value)}
              taskIdsWithComments={taskIdsWithComments}
            />
          </motion.section>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {activeTask && (
          <motion.section
            className={`${styles.taskDetailsRight}`}
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: taskPane.width, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{
              // No spring while dragging, or the pane lags behind the pointer.
              width: taskPane.isResizing
                ? { duration: 0 }
                : { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            style={{ zIndex: 9999 }}
          >
            <ResizeHandle pane={taskPane} label="Resize task panel" />
            <TaskDetailsPane template={template} task={activeTask} availableUsers={availableUsers} onTaskUpdated={handleTaskUpdated} saveTaskChanges={handleTaskSave} editMode={rolePermissions?.editGeneratedContent || false} />
          </motion.section>
        )}
      </AnimatePresence>

      <Popup isVisible={evidenceVisible} onClose={() => setEvidenceVisible(false)}>
        <LinkedEvidencePopup
          files={files}
          fileCount={fileCount}
          title={
            activeObjective?.title || 
            (evidenceObjectiveId ? improvementPlan?.improvements.find(imp => imp.id === evidenceObjectiveId)?.title : '') || 
            ''
          }
          loading={loading}
          error={error || undefined}
          canLinkEvidence={rolePermissions?.editGeneratedContent || false}
          roleName={rolePermissions?.roleTitle || ''}
          areaId={planId}
          linkPortfolio={
            planId && client?.id && currentUserSub && (evidenceObjectiveId || activeObjective?.id)
              ? {
                  areaId: planId,
                  questionId: evidenceObjectiveId || activeObjective?.id || '',
                  linkType: 'Improve',
                  clientId: client.id,
                  userSub: currentUserSub,
                  subjectNoun: 'objective',
                  onLinked: () => setEvidenceRefresh((value) => value + 1),
                }
              : undefined
          }
        />
      </Popup>

      <Popup
        isVisible={linkedDiagnosticsVisible}
        onClose={() => handleLinkedDiagnosticsVisible(false)}
      >
        <LinkedDiagnosticsPopup objectiveId={linkedDiagnosticsObjectiveId} />
      </Popup>

      {pendingItem && (
          <ConfirmAction
            isVisible={pendingItem !== undefined}
            onClose={() => {
              setPendingItem(undefined);
            }}
            clickHandler={deleteObjectiveHandler}
            title={`Delete Objective`}
            message={`Are you sure you want to delete this objective? This action cannot be undone.`}
            severity={"danger"}
            icon={"/img/trash-red.svg"}
          />
      )}
    </div>
  )
}

export default ImprovementPlanView
